// src/pages/TaskList.js

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { collection, query, onSnapshot, orderBy, where, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { selectUserID, selectIsAdmin } from '../redux/slice/authSlice'; 
import TaskItem from '../components/TaskItem';
import TaskForm from '../components/TaskForm';
import LoaderAI from '../components/loader/LoaderAI';
import { Grid, Typography, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import { SET_TASKS, TOGGLE_COMPLETE } from '../redux/slice/taskSlice';
import { GoogleGenerativeAI } from '@google/generative-ai';

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

const TaskList = () => {
  const dispatch = useDispatch();
  const userID = useSelector(selectUserID);
  const isAdmin = useSelector(selectIsAdmin); 
  const currentTasks = useSelector((state) => state.tasks.tasks);

  const [loading, setLoading] = useState(true);
  const [openTaskForm, setOpenTaskForm] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [openAIDialog, setOpenAIDialog] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);

  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [taskToDeleteId, setTaskToDeleteId] = useState(null);

  useEffect(() => {
    if (!userID) { 
      dispatch(SET_TASKS([]));
      setLoading(false);
      return;
    }

    setLoading(true);

    let tasksQuery = collection(db, 'tasks');

    if (!isAdmin) {
     
      tasksQuery = query(tasksQuery, where('userId', '==', userID));
    }
    
    tasksQuery = query(tasksQuery, orderBy('createdAt', 'desc'));


    const unsubscribe = onSnapshot(tasksQuery, (snapshot) => { 
      const tasksData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      dispatch(SET_TASKS(tasksData));
      setLoading(false);
    }, (error) => {
      console.error("Error fetching tasks:", error);
      toast.error("Failed to load tasks.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userID, isAdmin, dispatch]); 


  const handleEdit = (id) => {
    const task = currentTasks.find(t => t.id === id);
    setTaskToEdit(task);
    setOpenTaskForm(true);
  };

  const handleDelete = (id) => {
    setTaskToDeleteId(id);
    setOpenDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!taskToDeleteId) return;

    try {
      await deleteDoc(doc(db, 'tasks', taskToDeleteId));
      toast.success("Task deleted successfully!");
    } catch (error) {
      console.error("Error deleting task:", error);
      if (error.code === 'permission-denied') {
        toast.error("You do not have permission to delete this task.");
      } else {
        toast.error("Failed to delete task.");
      }
    } finally {
      setOpenDeleteConfirm(false);
      setTaskToDeleteId(null);
    }
  };

  const toggleComplete = async (taskId) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      const taskToToggle = currentTasks.find(task => task.id === taskId);

      if (!taskToToggle) {
        toast.error("Task not found!");
        return;
      }

      const newCompletedStatus = !taskToToggle.completed;

      await updateDoc(taskRef, {
        completed: newCompletedStatus
      });

      dispatch(TOGGLE_COMPLETE(taskId));
      toast.success(`Task marked as ${newCompletedStatus ? 'completed' : 'incomplete'}!`);
    } catch (error) {
      console.error("Error toggling task completion:", error);
      toast.error("Failed to toggle task completion.");
    }
  };

  const handleGetAIHelp = async (task) => {
    if (!GEMINI_API_KEY) {
        toast.error("Gemini API Key not found. Please check your .env.local file.");
        return;
    }

    setOpenAIDialog(true);
    setAiSuggestions(null);
    setIsGeneratingAI(true);
    toast.info("Generating AI suggestions...");

    try {
        const genAI = new GoogleGenerativeAI(GEMINI_API_KEY);
        const model = genAI.getGenerativeModel({ model: "gemini-2.5-flash" });

        const prompt = `You are a helpful and motivating AI task assistant. Your goal is to help a user break down or get unstuck on an incomplete task.
        The task details are:
        Title: "${task.title}"
        Description: "${task.description}"
        ${task.category ? `Category: "${task.category}"` : ''}
        ${task.sentimentScore ? `Sentiment (of description): "${task.sentimentScore}" (from -5 to 5)` : ''}
        ${task.extractedEntities && task.extractedEntities.length > 0 ? `Key Entities: ${task.extractedEntities.join(', ')}` : ''}

        Based on this information, provide actionable, concise suggestions to help the user complete or make progress on this task. Offer 3-5 bullet points.
        Focus on breaking down the task, identifying next steps, or suggesting resources.

        Example suggestions:
        - Break this into smaller, manageable sub-tasks.
        - Identify the very next micro-action you can take.
        - What's the biggest blocker? How can you overcome it?
        - Research relevant resources or tools.
        `;

        const result = await model.generateContent(prompt);
        const response = await result.response;
        const text = response.text();

        setAiSuggestions(text);
        toast.success("AI suggestions generated!");

    } catch (error) {
        console.error("Error generating AI suggestions:", error);
        toast.error(`Failed to get AI suggestions: ${error.message}`);
        setAiSuggestions("Failed to generate suggestions. Please try again later.");
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const handleCloseAIDialog = () => {
    setOpenAIDialog(false);
    setAiSuggestions(null);
  };

  if (loading) {
    return <LoaderAI />;
  }

  return (
    <Box sx={{ mt: 3, p: 2 }}>
      <Typography variant="h4" component="h2" gutterBottom>
        {isAdmin ? "All Tasks (Admin View)" : "Your Tasks"} 
      </Typography>
      {!isAdmin && ( 
        <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={() => { setTaskToEdit(null); setOpenTaskForm(true); }}
            >
              Add New Task
            </Button>
          </Grid>
        </Grid>
      )}

      {currentTasks.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          {isAdmin ? "No tasks found in the system." : "No tasks yet. Click 'Add New Task' to get started!"}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {currentTasks.map(task => (
            <Grid item xs={12} sm={6} md={4} key={task.id}>
              <TaskItem
                task={task}
                handleDelete={handleDelete}
                handleEdit={handleEdit}
                toggleComplete={toggleComplete}
                onGetAIHelp={handleGetAIHelp}
              />
            </Grid>
          ))}
        </Grid>
      )}

      
      {!isAdmin && ( 
        <TaskForm
          open={openTaskForm}
          handleClose={() => setOpenTaskForm(false)}
          taskToEdit={taskToEdit}
        />
      )}


     
      <Dialog open={openAIDialog} onClose={handleCloseAIDialog} maxWidth="md" fullWidth>
        <DialogTitle>AI Task Assistant</DialogTitle>
        <DialogContent dividers>
          {isGeneratingAI ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Generating intelligent suggestions...</Typography>
            </Box>
          ) : (
            aiSuggestions ? (
              <Typography component="div" dangerouslySetInnerHTML={{ __html: aiSuggestions.replace(/\n/g, '<br/>') }} />
            ) : (
              <Typography>No suggestions available.</Typography>
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAIDialog} color="primary">Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          Are you sure you want to permanently delete this task? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirm(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskList;
